import { useCallback, useEffect, useRef, useState } from "react";
import { Archive, ArrowLeft, Search, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import FooterNav from "../components/FooterNav";
import { fetchSpaces, type Space } from "../api/spaces";
import SpaceCommunityCard from "../features/spaces/SpaceCommunityCard";
import { readSpacesDirectoryMemory, rememberSpacesDirectoryMemory } from "../features/spaces/listMemory";
import { readSpacesBackState, spacesBackState } from "../features/spaces/navigation";
import { useSpaceQueryState } from "../features/spaces/useSpaceQueryState";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";

export default function ArchivedSpaces() {
    const navigate = useNavigate();
    const location = useLocation();
    const cachedListRef = useRef(readSpacesDirectoryMemory("archives"));
    const restoredScrollRef = useRef(false);
    const mainRef = useRef<HTMLElement | null>(null);
    const [spaces, setSpaces] = useState<Space[]>(() => cachedListRef.current?.spaces || []);
    const [page, setPage] = useState(() => cachedListRef.current?.page || 1);
    const [hasMore, setHasMore] = useState(() => cachedListRef.current?.hasMore ?? true);
    const [loading, setLoading] = useState(() => !cachedListRef.current);
    const [error, setError] = useState<string | null>(null);
    const { searchInput, searchQuery, setSearchInput } = useSpaceQueryState({ debounceMs: 300, initialSearch: cachedListRef.current?.searchInput || "" });
    const backTarget = readSpacesBackState(location.state);
    const currentBackState = spacesBackState(location, "Space Archives");

    const loadFirstPage = useCallback(async () => {
        const cachedForQuery = Boolean(cachedListRef.current?.spaces.length && cachedListRef.current.searchQuery === searchQuery);
        setLoading(true);
        setError(null);
        try {
            const data = await fetchSpaces({ page: 1, limit: 30, search: searchQuery, archived: true });
            const archived = data.spaces.filter((space) => space.isArchived);
            if (cachedForQuery) {
                const refreshedIds = new Set(archived.map((space) => space.id));
                setSpaces((current) => [...archived, ...current.filter((space) => !refreshedIds.has(space.id))]);
                setPage((current) => Math.max(current, 2));
                setHasMore((current) => current && data.hasMore);
            } else {
                setSpaces(archived);
                setPage(2);
                setHasMore(data.hasMore);
            }
        } catch (err: any) {
            setError(err?.message || "Could not load archived Spaces.");
        } finally {
            setLoading(false);
        }
    }, [searchQuery]);

    const loadMore = useCallback(async () => {
        if (loading || !hasMore) return;
        setLoading(true);
        try {
            const data = await fetchSpaces({ page, limit: 30, search: searchQuery, archived: true });
            setSpaces((current) => {
                const known = new Set(current.map((space) => space.id));
                return [...current, ...data.spaces.filter((space) => space.isArchived && !known.has(space.id))];
            });
            setPage((current) => current + 1);
            setHasMore(data.hasMore);
        } catch (err: any) {
            setError(err?.message || "Could not load more archived Spaces.");
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [hasMore, loading, page, searchQuery]);

    useEffect(() => {
        loadFirstPage();
    }, [loadFirstPage]);

    useEffect(() => {
        rememberSpacesDirectoryMemory("archives", {
            spaces,
            page,
            hasMore,
            searchInput,
            searchQuery,
        });
    }, [hasMore, page, searchInput, searchQuery, spaces]);

    useEffect(() => {
        const cached = cachedListRef.current;
        if (restoredScrollRef.current || !cached?.scrollTop || !mainRef.current || (loading && !spaces.length)) return;
        restoredScrollRef.current = true;
        window.requestAnimationFrame(() => {
            if (mainRef.current) mainRef.current.scrollTop = cached.scrollTop;
        });
    }, [loading, spaces.length]);

    function rememberScroll(scrollTop = mainRef.current?.scrollTop || 0) {
        rememberSpacesDirectoryMemory("archives", { scrollTop });
    }

    function openSpace(id: string) {
        rememberScroll();
        navigate(`/spaces/${id}`, { state: currentBackState });
    }

    const sentinelRef = useInfiniteScroll(loadMore, hasMore && !loading);

    return (
        <div className="bs-mobile-spaces flex h-screen flex-col overflow-hidden bg-black text-white">
            <header className="sticky top-0 z-30 border-b border-neutral-800 bg-black/95 px-4 py-4 backdrop-blur">
                <div className="mx-auto max-w-7xl">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div className="flex min-w-0 items-center gap-3">
                            <button type="button" onClick={() => navigate(backTarget.path, { replace: true })} className="bs-all-spaces-back" aria-label={`Back to ${backTarget.label}`}>
                                <ArrowLeft size={18} />
                            </button>
                            <div className="bs-space-archives-mark">
                                <Archive size={22} strokeWidth={2.45} />
                            </div>
                            <div className="min-w-0">
                                <h1 className="truncate text-2xl font-bold tracking-normal text-white">Space Archives</h1>
                            </div>
                        </div>

                        <label className="bs-spaces-search-shell is-archive flex h-11 min-w-0 items-center gap-3 rounded-lg border border-neutral-800 bg-neutral-950 px-3 md:w-80 focus-within:border-amber-500/70">
                            <Search size={17} className="text-neutral-500" />
                            <span className="sr-only">Search archived spaces</span>
                            <input
                                type="search"
                                value={searchInput}
                                onChange={(event) => setSearchInput(event.target.value)}
                                placeholder="Search archives"
                                className="bs-spaces-search-input min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-neutral-600"
                            />
                            {searchInput && <button type="button" onClick={() => setSearchInput("")} className="text-neutral-500 hover:text-white" aria-label="Clear search"><X size={15} /></button>}
                        </label>
                    </div>
                </div>
            </header>

            <main ref={mainRef} onScroll={(event) => rememberScroll(event.currentTarget.scrollTop)} className="bs-spaces-smooth-list flex-1 overflow-y-auto">
                <div className="mx-auto max-w-7xl pb-28 pt-6">
                    {error && <div role="alert" className="mb-6 rounded-xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-sm text-red-100">{error}</div>}

                    {!loading && spaces.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center">
                            <h2 className="font-black">No archives found</h2>
                            <p className="mt-1 text-sm text-neutral-500">Search another name, tag, or creator.</p>
                        </div>
                    ) : (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {spaces.map((space) => (
                                <SpaceCommunityCard
                                    key={space.id}
                                    space={space}
                                    onOpen={openSpace}
                                />
                            ))}
                            {loading && spaces.length === 0 && Array.from({ length: 8 }).map((_, index) => (
                                <div key={`loading-${index}`} className="h-60 animate-pulse rounded-2xl border border-white/10 bg-neutral-950" />
                            ))}
                        </div>
                    )}

                    <div ref={sentinelRef} className="h-8" aria-hidden="true" />
                    {!hasMore && spaces.length > 0 && <p className="py-5 text-center text-xs font-semibold text-neutral-600">You have reached the end.</p>}
                </div>
            </main>
            <FooterNav />
        </div>
    );
}
